/**
 * What a plan can switch on — `docs/rowted-admin-v2.md` §5.4.
 *
 * `tenant_features` (migration 0066) is a key/enabled pair per tenant and its
 * `key` is free text, deliberately: the set grows and a migration per switch
 * is a migration nobody will write. But free text with no catalogue means the
 * Admin screen that sets features by tier and the client screen that turns
 * them on have no shared vocabulary — a typo on one side is a feature that
 * silently never enables, which is the worst kind of bug to find.
 *
 * So this is the list, here in `shared` for the reason the permission matrix
 * is: the API and both UIs read the same table and cannot disagree.
 *
 * **A feature is a capability somebody sells or withholds**, not a permission
 * and not a setting. What a *person* may do is `permissions.ts`; how a company
 * has configured something it already has is its own settings. This is the
 * third thing: whether the company has it at all.
 */
export const FEATURES = [
    { key: 'quotes', label: 'Quoting', sub: 'Quotes, packages and proposals', core: true },
    { key: 'scheduling', label: 'Scheduling', sub: 'The crew board and visits', core: true },
    { key: 'invoicing', label: 'Invoicing', sub: 'Invoices, payments and AR', core: true },
    { key: 'ai', label: 'Rowted AI', sub: 'The assistant and smart import', needs: 'azure_openai' },
    { key: 'rentals', label: 'Rentals', sub: 'Rental periods and open-ended work' },
    { key: 'inventory', label: 'Inventory and purchasing', sub: 'Yards, counts and POs' },
    { key: 'equipment', label: 'Equipment', sub: 'Assignment, service and utilisation' },
    /*
     * The crew's iPad (`docs/visit-page.md` §27.1): punching, closing out a day,
     * receipts and photos from the field. The Visit page's crew capabilities are
     * on only where a company has this (`MODULE_FEATURE.crew`), so it is in every
     * tier and migration 0155 switched it on for every company that already
     * existed — the iPad punched for all of them before it had a name here.
     */
    { key: 'crew_app', label: 'Crew app', sub: 'The crew iPad: punches, close-outs and photos' },
    { key: 'portal', label: 'Customer portal', sub: 'Where a customer approves and pays' },
    { key: 'quickbooks', label: 'QuickBooks', sub: 'Two-way sync of invoices and payments', needs: 'qbo' },
    { key: 'sms', label: 'Text messages', sub: 'Reminders, pay links and crew alerts', needs: 'twilio' },
    { key: 'reporting', label: 'Reporting', sub: 'Margin, utilisation and the dashboards' },
    /*
     * Measuring a site, which `company-settings.md` lists as an onboarding
     * feature: measurements on properties, the measure field on a package, rate
     * tiers, and Measure on a map. It was referenced by key in two documents
     * and present in neither this list nor any tier, which is exactly the
     * silent-typo failure this file exists to prevent.
     */
    { key: 'measurements', label: 'Measurements',
        sub: 'Measure a site, and price by the square foot' },
    /*
     * `needs: 'maps'` means the Map Tiles key is set (`integration-config.ts`).
     * Without it the tab can still show a map that already exists — the "stays"
     * rule: turning a feature off never hides data somebody made — but the
     * aerial imagery behind it will not load, so the dependency is named rather
     * than discovered at the moment somebody tries to draw.
     */
    { key: 'property_maps', label: 'Property maps',
        sub: 'Measure a site, and draw plans the crews read', needs: 'maps' },
];
/** Every key, for validating what a tier stores. */
export const FEATURE_KEYS = FEATURES.map((one) => one.key);
/** The ones a tier cannot withhold, so the checklist does not offer them. */
export const CORE_FEATURES = FEATURES.filter((one) => one.core).map((one) => one.key);
/** The ones a price list actually chooses between. */
export const SELLABLE_FEATURES = FEATURES.filter((one) => !one.core);
export const featureByKey = (key) => FEATURES.find((one) => one.key === key);
/**
 * What a tier's stored list means, resolved.
 *
 * Core is always in, whatever the list says, and an unknown key is dropped
 * rather than passed through — a feature that was removed from the product
 * should not keep switching something on by name.
 */
export function featuresOf(stored) {
    const named = (stored ?? []).filter((key) => FEATURE_KEYS.includes(key));
    return [...new Set([...CORE_FEATURES, ...named])];
}
/**
 * Board 7's Features column: "Core", "Core · AI · Rentals", "Everything".
 *
 * Words rather than a count, because "3 features" tells somebody comparing two
 * tiers nothing about which three.
 */
export function featureWords(stored) {
    const extra = featuresOf(stored).filter((key) => !CORE_FEATURES.includes(key));
    if (extra.length === 0)
        return 'Core';
    if (extra.length === SELLABLE_FEATURES.length)
        return 'Everything';
    return ['Core', ...extra.map((key) => featureByKey(key)?.label ?? key)].join(' · ');
}
//# sourceMappingURL=features.js.map